import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import type { SyntheticEvent } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CustomTreeItem } from "../bits/treeMenu";
import {
  extractSectionPath,
  getParentIds,
  shallowEqualArray,
  transformToTree,
  useDebounce,
} from "../utils";
import { ArticleContext } from "../contexts/WithArticle/ArticleContext";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { BookmarkContext } from "../contexts/WithBookmarks/BookmarkContext";
import { EreaderMenuPanel } from "../bits/EreaderMenuPanel";

export const ArticleNavPanel = ({ open }: { open: boolean }) => {
  const {
    contents,
    selectSection,
    currentSection,
    setScrollDisabled,
    setPendingHash,
    scrollSnippet,
    articleSlug,
    ghostPost,
  } = useContext(ArticleContext);
  const { annotations, annotationsOpen } = useContext(AnnotationContext);
  const { bookmarksByPublication } = useContext(BookmarkContext);
  const navigate = useNavigate();
  const apiRef = useTreeViewApiRef();
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
  const slots = useMemo(() => ({ item: CustomTreeItem }), []);

  const selectAndHighlightItem = useCallback(
    (_: SyntheticEvent, itemId: string | null) => {
      if (itemId && apiRef.current) {
        const item = apiRef.current.getItem(itemId);
        if (item?.menuType === "issue") {
          if (itemId !== articleSlug) {
            setLoadingItemId(itemId);
          }
          navigate(`/article/${itemId}`);
          return;
        }
        setScrollDisabled(true);
        setPendingHash(undefined);
        selectSection(itemId, item);
        setSelectedItem(itemId);
        if (item.children?.length) {
          setExpandedItems((prev) => {
            if (item.children?.length) {
              return prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId];
            }
            return prev;
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiRef, articleSlug, navigate, selectSection, setScrollDisabled],
  );

  useEffect(() => {
    setLoadingItemId(null);
  }, [articleSlug]);

  const tocItems = useMemo(() => {
    if (!contents) return [];
    return transformToTree(contents, annotations, bookmarksByPublication);
  }, [contents, annotations, bookmarksByPublication]);

  const menuItems = useMemo(() => {
    const issueArticles = ghostPost?.issueArticles;
    if (!issueArticles?.length) return tocItems;

    // The first tocItem is typically the article's root h1 chapter whose title
    // duplicates the issue entry above it — skip it and use its children directly.
    const firstToc = tocItems[0];
    const dedupedToc =
      tocItems.length === 1 && firstToc?.menuType === "chapter"
        ? (firstToc.children ?? [])
        : tocItems;

    return issueArticles.map((article) => ({
      id: article.slug,
      type: "issue",
      label: article.title ?? article.slug,
      menuType: "issue",
      author:
        article.authors
          ?.map((a) => a.name)
          .filter(Boolean)
          .join(", ") ?? null,
      children: article.slug === articleSlug ? dedupedToc : undefined,
    }));
  }, [ghostPost?.issueArticles, tocItems, articleSlug]);

  const debounceScroll = useDebounce((currentSectionId: string) => {
    const el = document.getElementById(`nav-${currentSectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 100);

  useEffect(() => {
    if (currentSection && !scrollSnippet) {
      const sectionPath = extractSectionPath(currentSection.id);
      if (currentSection.id !== selectedItem) {
        if (sectionPath) {
          setSelectedItem(sectionPath);
        }
      }
      const parentIds = getParentIds(currentSection.id);
      if (currentSection.children?.length) {
        setExpandedItems((prev) =>
          shallowEqualArray(prev, [...parentIds, currentSection.id])
            ? prev
            : [...parentIds, currentSection.id],
        );
      } else {
        setExpandedItems((prev) =>
          shallowEqualArray(prev, parentIds) ? prev : parentIds,
        );
      }
      debounceScroll(sectionPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection?.id, open]);

  return (
    <EreaderMenuPanel span={3} isOpen={open}>
      <RichTreeView
        apiRef={apiRef}
        items={menuItems}
        onItemClick={selectAndHighlightItem}
        selectedItems={selectedItem}
        expandedItems={expandedItems}
        slots={slots}
        slotProps={{
          item: {
            annotationsOpen,
            publicationId: articleSlug,
            loadingItemId,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        }}
      />
    </EreaderMenuPanel>
  );
};
